import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Briefcase,
  ChevronRight,
  Clock,
  Globe,
  Loader2,
  MapPin,
  Search,
  Star,
  Users,
  X,
  Zap,
} from 'lucide-react';
import careersService from '../services/careers.service';
import type { PublicCareersData, PublicJDItem, ParsedJDData } from '../types/careers';

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelativeDate(dateStr: string): string {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) { const w = Math.floor(diffDays / 7); return `${w} ${w === 1 ? 'week' : 'weeks'} ago`; }
  const m = Math.floor(diffDays / 30);
  return `${m} ${m === 1 ? 'month' : 'months'} ago`;
}

function getLocationText(parsed: ParsedJDData | null): string | null {
  if (!parsed?.location) return null;
  const { city, country, remote } = parsed.location;
  if (remote && !city && !country) return 'Remote';
  const parts = [city, country].filter(Boolean).join(', ');
  if (remote && parts) return `${parts} · Remote`;
  return parts || null;
}

export function CareersListPage() {
  const { workspaceCode } = useParams<{ workspaceCode: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<PublicCareersData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterLevel, setFilterLevel] = useState<string | null>(null);
  const [filterRemote, setFilterRemote] = useState(false);

  useEffect(() => {
    if (!workspaceCode) return;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        setData(await careersService.getCareers(workspaceCode));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load careers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [workspaceCode]);

  const filteredItems = data?.items.filter((jd) => {
    const parsed = jd.parsed_data as ParsedJDData | null;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const title = (parsed?.title || jd.name).toLowerCase();
      const location = getLocationText(parsed)?.toLowerCase() || '';
      const type = (parsed?.employment_type || '').toLowerCase();
      if (!title.includes(q) && !location.includes(q) && !type.includes(q)) return false;
    }
    if (filterType && parsed?.employment_type !== filterType) return false;
    if (filterLevel && parsed?.experience_level !== filterLevel) return false;
    if (filterRemote && !parsed?.location?.remote) return false;
    return true;
  }) ?? [];

  const activeFilterCount = [filterType, filterLevel, filterRemote || null].filter(Boolean).length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-7 w-7 animate-spin text-orange-500 mx-auto" />
          <p className="mt-3 text-gray-400 text-sm">Loading positions...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">{error || 'Careers page not found'}</h2>
          <p className="mt-2 text-gray-400 text-sm">Please check the URL and try again.</p>
        </div>
      </div>
    );
  }

  // Derive filter options from items
  const availableTypes = [...new Set(
    data.items.map((jd) => (jd.parsed_data as ParsedJDData | null)?.employment_type).filter(Boolean) as string[]
  )];
  const availableLevels = [...new Set(
    data.items.map((jd) => (jd.parsed_data as ParsedJDData | null)?.experience_level).filter(Boolean) as string[]
  )];
  const hasRemote = data.items.some((jd) => (jd.parsed_data as ParsedJDData | null)?.location?.remote);

  // Derive stats from items
  const remoteCount = data.items.filter(
    (jd) => (jd.parsed_data as ParsedJDData | null)?.location?.remote
  ).length;
  const types = [...new Set(
    data.items
      .map((jd) => (jd.parsed_data as ParsedJDData | null)?.employment_type)
      .filter(Boolean)
  )];
  const companyInfo = data.items
    .map((jd) => (jd.parsed_data as ParsedJDData | null)?.company_info)
    .find((c) => c?.name || c?.industry || c?.description);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero header */}
      <header className="bg-white border-b border-gray-100">
        {/* Top bar */}
        <div className="border-b border-gray-50 py-3">
          <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">{data.workspace_name}</span>
            <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 border border-orange-100 text-xs font-semibold px-3 py-1 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse" />
              Hiring
            </span>
          </div>
        </div>

        {/* Main hero */}
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Come work with us
          </h1>
          <p className="mt-3 text-gray-400 text-base max-w-md mx-auto">
            {companyInfo?.description
              ? companyInfo.description
              : `Join ${data.workspace_name} and help us build something great.`}
          </p>

          {/* Stats row */}
          {data.total > 0 && (
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <Stat icon={<Briefcase className="h-4 w-4" />} value={data.total} label={data.total === 1 ? 'Open position' : 'Open positions'} />
              {remoteCount > 0 && (
                <Stat icon={<Globe className="h-4 w-4" />} value={remoteCount} label={remoteCount === 1 ? 'Remote role' : 'Remote roles'} />
              )}
              {types.length > 0 && (
                <Stat icon={<Users className="h-4 w-4" />} value={types.length} label={types.length === 1 ? 'Work type' : 'Work types'} />
              )}
              {companyInfo?.industry && (
                <Stat icon={<Zap className="h-4 w-4" />} value={companyInfo.industry} label="Industry" />
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by title, location, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 shadow-sm"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter chips */}
        {(availableTypes.length > 0 || availableLevels.length > 0 || hasRemote) && (
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {availableTypes.map((type) => (
              <FilterChip
                key={type}
                label={formatLabel(type)}
                active={filterType === type}
                onClick={() => setFilterType(filterType === type ? null : type)}
              />
            ))}

            {availableTypes.length > 0 && (availableLevels.length > 0 || hasRemote) && (
              <span className="h-4 w-px bg-gray-200" />
            )}

            {availableLevels.map((level) => (
              <FilterChip
                key={level}
                label={level}
                active={filterLevel === level}
                onClick={() => setFilterLevel(filterLevel === level ? null : level)}
                color="blue"
              />
            ))}

            {hasRemote && (
              <FilterChip
                label="Remote"
                active={filterRemote}
                onClick={() => setFilterRemote(!filterRemote)}
                color="emerald"
              />
            )}

            {activeFilterCount > 0 && (
              <>
                <span className="h-4 w-px bg-gray-200" />
                <button
                  onClick={() => { setFilterType(null); setFilterLevel(null); setFilterRemote(false); }}
                  className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                >
                  <X className="h-3 w-3" /> Clear filters
                </button>
              </>
            )}
          </div>
        )}

        {/* Results summary */}
        {(searchQuery.trim() || activeFilterCount > 0) && filteredItems.length > 0 && (
          <p className="text-xs text-gray-400 mb-4">
            {filteredItems.length} {filteredItems.length === 1 ? 'position' : 'positions'} found
          </p>
        )}

        {/* Empty states */}
        {data.total === 0 ? (
          <div className="text-center py-20">
            <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="h-7 w-7 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-800">No open positions</h3>
            <p className="mt-2 text-sm text-gray-400">Check back soon — new roles are added regularly.</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Search className="h-7 w-7 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-800">No matching positions</h3>
            <p className="mt-2 text-sm text-gray-400">Try a different keyword or adjust your filters.</p>
            <button
              onClick={() => { setSearchQuery(''); setFilterType(null); setFilterLevel(null); setFilterRemote(false); }}
              className="mt-4 text-sm text-orange-500 hover:text-orange-600 font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((jd) => (
              <JDCard
                key={jd.id}
                jd={jd}
                onClick={() => navigate(`/${workspaceCode}/jds/${jd.id}`)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-12">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-gray-700">{data.workspace_name}</p>
              {companyInfo?.industry && (
                <p className="text-xs text-gray-400 mt-0.5">{companyInfo.industry}</p>
              )}
            </div>
            <div className="flex flex-col items-center sm:items-end gap-1">
              <p className="text-xs text-gray-400">
                &copy; {new Date().getFullYear()} {data.workspace_name}. All rights reserved.
              </p>
              <p className="text-xs text-gray-300">Powered by AaaS</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  color = 'orange',
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  color?: 'orange' | 'blue' | 'emerald';
}) {
  const activeClass = {
    orange: 'bg-orange-500 text-white border-orange-500',
    blue:   'bg-blue-500 text-white border-blue-500',
    emerald:'bg-emerald-500 text-white border-emerald-500',
  }[color];

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
        active ? activeClass : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
      }`}
    >
      {label}
      {active && <X className="h-3 w-3" />}
    </button>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number | string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5 text-orange-500">{icon}
        <span className="text-lg font-bold text-gray-900">{value}</span>
      </div>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}

interface JDCardProps {
  jd: PublicJDItem;
  onClick: () => void;
}

function JDCard({ jd, onClick }: JDCardProps) {
  const parsed = jd.parsed_data as ParsedJDData | null;
  const title = parsed?.title || jd.name;
  const location = getLocationText(parsed);
  const skills = parsed?.skills_required?.slice(0, 5) ?? [];
  const extraSkills = (parsed?.skills_required?.length ?? 0) - 5;

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-gray-900 group-hover:text-orange-600 transition-colors truncate">
            {title}
          </h2>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
            {location && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                {location}
              </span>
            )}
            {parsed?.employment_type && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Briefcase className="h-3 w-3 flex-shrink-0" />
                {formatLabel(parsed.employment_type)}
              </span>
            )}
            {parsed?.experience_level && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Star className="h-3 w-3 flex-shrink-0" />
                {parsed.experience_level}
              </span>
            )}
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="h-3 w-3 flex-shrink-0" />
              {formatRelativeDate(jd.created_at)}
            </span>
          </div>
        </div>

        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-orange-500 flex-shrink-0 mt-0.5 transition-colors" />
      </div>

      {(parsed?.location?.remote || parsed?.employment_type || parsed?.experience_level) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {parsed?.location?.remote && (
            <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-2.5 py-0.5 text-xs font-medium">
              Remote
            </span>
          )}
          {parsed?.employment_type && (
            <span className="bg-orange-50 text-orange-700 border border-orange-100 rounded-full px-2.5 py-0.5 text-xs font-medium">
              {formatLabel(parsed.employment_type)}
            </span>
          )}
          {parsed?.experience_level && (
            <span className="bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2.5 py-0.5 text-xs font-medium">
              {parsed.experience_level}
            </span>
          )}
        </div>
      )}

      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-50">
          {skills.map((skill, i) => (
            <span key={i} className="bg-slate-50 text-slate-600 border border-slate-100 rounded-lg px-2.5 py-0.5 text-xs font-medium">
              {skill}
            </span>
          ))}
          {extraSkills > 0 && (
            <span className="bg-slate-50 text-slate-400 border border-slate-100 rounded-lg px-2.5 py-0.5 text-xs">
              +{extraSkills} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}
